<template>
  <div class="union-screen-page" v-loading="loadingDetail">
    <div class="page-title">水务大脑大屏</div>

    <div class="section-card">
      <div class="section-title">供水大脑</div>
      <div class="switch-row">
        <span class="switch-label">水源地模块</span>
        <el-switch v-model="waterSource" @change="handleWaterSourceChange" />
      </div>
    </div>

    <div class="section-card">
      <div class="section-title">水务大脑菜单权限</div>
      <el-tree
        ref="treeRef"
        :data="treeData"
        :props="treeProps"
        node-key="key"
        show-checkbox
        default-expand-all
        @check="handleTreeCheck"
      />
    </div>
  </div>
</template>

<script>
import {
  defineComponent,
  getCurrentInstance,
  nextTick,
  onActivated,
  onMounted,
  reactive,
  ref,
  toRefs,
} from "vue";

const MODULE_ITEMS = [
  { key: "supplyBrain", label: "供水大脑" },
  { key: "dispatchBrain", label: "供水调度大脑" },
  { key: "pipeNetworkBrain", label: "管网大脑" },
  { key: "marketingBrain", label: "营销大脑" },
  { key: "manageBrain", label: "经营大脑" },
  { key: "emergencyBrain", label: "应急大脑" },
  { key: "securityBrain", label: "安防大脑" },
];

export default defineComponent({
  name: "UnionScreen",
  setup() {
    const { proxy } = getCurrentInstance();
    const treeRef = ref();
    const state = reactive({
      loadingDetail: false,
      saving: false,
      syncingTree: false,
      initialized: false,
      activatedOnce: false,
      lastSavedPayload: "",
      pendingPayload: null,
      waterSource: false,
      moduleConfig: {
        supplyBrain: false,
        dispatchBrain: false,
        pipeNetworkBrain: false,
        marketingBrain: false,
        manageBrain: false,
        emergencyBrain: false,
        securityBrain: false,
      },
      treeProps: {
        label: "label",
        children: "children",
      },
      treeData: [
        {
          key: "root",
          label: "根节点",
          children: MODULE_ITEMS.map((item) => ({
            key: item.key,
            label: item.label,
          })),
        },
      ],
    });

    const buildPayload = () => {
      return {
        waterSource: !!state.waterSource,
        supplyBrain: !!state.moduleConfig.supplyBrain,
        dispatchBrain: !!state.moduleConfig.dispatchBrain,
        pipeNetworkBrain: !!state.moduleConfig.pipeNetworkBrain,
        marketingBrain: !!state.moduleConfig.marketingBrain,
        manageBrain: !!state.moduleConfig.manageBrain,
        emergencyBrain: !!state.moduleConfig.emergencyBrain,
        securityBrain: !!state.moduleConfig.securityBrain,
      };
    };

    const setTreeCheckedFromState = async () => {
      if (!treeRef.value) {
        return;
      }
      state.syncingTree = true;
      const checkedKeys = MODULE_ITEMS.filter(
        (item) => state.moduleConfig[item.key]
      ).map((item) => item.key);
      treeRef.value.setCheckedKeys(checkedKeys);
      await nextTick();
      state.syncingTree = false;
    };

    const fetchDetail = async () => {
      state.loadingDetail = true;
      try {
        const res = await proxy.$api.getModuleConfigDetail();
        const detail = res?.data || {};
        state.waterSource = !!detail.waterSource;
        MODULE_ITEMS.forEach((item) => {
          state.moduleConfig[item.key] = !!detail[item.key];
        });
        await nextTick();
        await setTreeCheckedFromState();
        state.lastSavedPayload = JSON.stringify(buildPayload());
      } finally {
        state.loadingDetail = false;
      }
    };

    const submitConfigByPayload = async (payload) => {
      const payloadText = JSON.stringify(payload);
      if (payloadText === state.lastSavedPayload) {
        return;
      }
      if (state.saving) {
        state.pendingPayload = payload;
        return;
      }
      state.saving = true;
      try {
        await proxy.$api.postModuleConfig(payload);
        state.lastSavedPayload = payloadText;
      } catch (error) {
        state.pendingPayload = null;
        proxy.$message.error("配置保存失败，请稍后重试");
        await fetchDetail();
      } finally {
        state.saving = false;
        if (state.pendingPayload) {
          const nextPayload = state.pendingPayload;
          state.pendingPayload = null;
          await submitConfigByPayload(nextPayload);
        }
      }
    };

    const handleWaterSourceChange = () => {
      if (state.loadingDetail) {
        return;
      }
      submitConfigByPayload(buildPayload());
    };

    const handleTreeCheck = (data, checkedState) => {
      if (state.syncingTree || state.loadingDetail) {
        return;
      }
      MODULE_ITEMS.forEach((item) => {
        state.moduleConfig[item.key] = checkedState.checkedKeys.includes(
          item.key
        );
      });
      submitConfigByPayload(buildPayload());
    };

    onMounted(async () => {
      await fetchDetail();
      state.initialized = true;
    });

    onActivated(() => {
      if (!state.activatedOnce) {
        state.activatedOnce = true;
        return;
      }
      if (state.initialized) {
        fetchDetail();
      }
    });

    return {
      ...toRefs(state),
      treeRef,
      handleTreeCheck,
      handleWaterSourceChange,
    };
  },
});
</script>

<style scoped lang="less">
.union-screen-page {
  min-height: 100%;
  padding: 16px;
  background: #f1f3f6;
}

.page-title {
  margin-bottom: 14px;
  font-size: 16px;
  font-weight: 700;
  color: #1f2d3d;
}

.section-card {
  margin-bottom: 14px;
  padding: 16px;
  background: #ffffff;
  border-radius: 2px;
}

.section-title {
  margin-bottom: 16px;
  padding-left: 10px;
  border-left: 4px solid #2d63ff;
  font-size: 16px;
  font-weight: 700;
  color: #1f2d3d;
  line-height: 1;
}

.switch-row {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-left: 14px;
}

.switch-label {
  font-size: 14px;
  color: #333333;
}

:deep(.el-tree-node__label) {
  font-size: 14px;
}
</style>
